import React, { useCallback, useEffect, useRef } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import { useOutletContext } from "react-router-dom";

import { useDispatch, useSelector } from "../../hooks";

import AlertContainer from "../../components/common/AlertContainer";
import SpinningCircle from "../../components/common/SpinningCircle";
import { Digit } from "../../components/controls/Digit";
import { SubmitButton } from "../../components/controls/SubmitButton";
import { KEY_MAPPING } from "../../utils/keyCodes";
import { svgIcon } from "../../utils/svgProvider";
import { sendCode, validateSmsCode } from "./slices/smslogin.actions";
import { onAlertClose, triggerAlert } from "./slices/smslogin.slice";
import {
	OTPCredential,
	OTPCredentialRequestOptions,
	RouteContext,
} from "./slices/smslogin.types";

const ABORT_CONTROLLER_TIMEOUT = 2 * 60000;

export const SmsLoginForm = () => {
	const context = useOutletContext<RouteContext>();
	const dispatch = useDispatch();

	const { formatMessage } = useIntl();

	const submitButtonRef = useRef(null);

	const fieldsRefArray = [
		"code-1",
		"code-2",
		"code-3",
		"code-4",
		"code-5",
		"code-6",
	] as const;

	const schema = yup.object({
		"code-1": yup.string().required("komid.register.validation.required"),
		"code-2": yup.string().required("komid.register.validation.required"),
		"code-3": yup.string().required("komid.register.validation.required"),
		"code-4": yup.string().required("komid.register.validation.required"),
		"code-5": yup.string().required("komid.register.validation.required"),
		"code-6": yup.string().required("komid.register.validation.required"),
	});

	const { register, handleSubmit, setValue, setFocus } = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		resolver: yupResolver(schema),
	});

	const isFetching = useSelector((state) => state.smslogin.isFetching);
	const alerts = useSelector((state) => state.smslogin.alerts);
	const returnUrl = useSelector((state) => state.smslogin.form.returnUrl);
	const email = useSelector((state) => state.smslogin.form.email);
	const maskedPhoneNumber = context.maskedPhoneNumber;

	const setInputValues = useCallback(
		(values) => {
			for (let index = 0; index <= fieldsRefArray.length; index++) {
				if (!isNaN(values[index])) {
					setValue(fieldsRefArray[index], values[index]);
				}
			}
		},
		[fieldsRefArray, setValue],
	);

	const handleWebOtpCode = useCallback(() => {
		if ("OTPCredential" in window) {
			const controller = new AbortController();

			setTimeout(() => {
				controller.abort();
			}, ABORT_CONTROLLER_TIMEOUT);

			navigator.credentials
				.get({
					otp: { transport: ["sms"] },
					signal: controller.signal,
				} as OTPCredentialRequestOptions)
				.then((otp: OTPCredential) => {
					const codeArray = otp.code.split("");

					setInputValues(codeArray);
					submitButtonRef.current.focus();
					return false;
				});
		}
	}, [setInputValues]);

	useEffect(() => {
		handleWebOtpCode();
	}, [handleWebOtpCode]);

	const onSubmit = handleSubmit(
		(data) => {
			const smsCode = Object.values(data).join("");

			dispatch(
				validateSmsCode({
					smsCode,
					email,
					returnUrl,
				}),
			);
		},
		() => {
			const notEmptyCodeError = "komid.smslogin.code.not.empty.error";
			if (!alerts.some((alert) => alert.messageKey === notEmptyCodeError)) {
				dispatch(
					triggerAlert([
						{
							level: "error",
							messageKey: notEmptyCodeError,
						},
					]),
				);
			}
		},
	);

	const onResentCodeClick = (e) => {
		e.preventDefault();

		fieldsRefArray.map((field) => {
			setValue(field, "");
		});

		dispatch(sendCode(email));
		handleWebOtpCode();
	};

	const onTextChange = (event, nextRef) => {
		const { id, value } = event.target;

		setValue(id, value);
		if (fieldsRefArray.includes(nextRef)) {
			setFocus(nextRef);
		} else {
			nextRef.current.focus();
			nextRef.current.select?.();
		}
	};

	const onKeyDown = ({ event, prevRef, nextRef }) => {
		const {
			target: { name },
		} = event;

		switch (event.key) {
			case KEY_MAPPING.BACKSPACE.KEY:
				setFocus(prevRef);
				setValue(name, "");
				break;
			case KEY_MAPPING.LEFT_ARROW.KEY:
				setFocus(prevRef);
				break;
			case KEY_MAPPING.RIGHT_ARROW.KEY:
				setFocus(nextRef);
				break;
			case KEY_MAPPING.ENTER.KEY:
				submitButtonRef.current.focus();
				submitButtonRef.current.click();
				break;
		}
	};

	const onCodePaste = ({ clipboardData }) => {
		const pastedCode = clipboardData.getData("text/plain").replaceAll(" ", "");
		const codeArray = pastedCode.split("");
		setInputValues(codeArray);
		submitButtonRef.current.focus();
	};

	return (
		<>
			<AlertContainer
				alerts={alerts}
				onAlertClose={(data) => dispatch(onAlertClose(data))}
			/>

			<div className="form-wrapper">
				<section className="form-section">
					<h1>{formatMessage({ id: "komid.smslogin.smsCodePageTitle" })}</h1>
					<h3>
						{formatMessage({ id: "komid.smslogin.hashedPhoneNumber.info" })}{" "}
						<div className="form-section-phone-infomation">
							<span>{maskedPhoneNumber}</span>
							<button
								onClick={onResentCodeClick}
								data-testid="code-resend-link"
							>
								{formatMessage({ id: "komid.smslogin.code.send.again" })}
								{svgIcon.refresh}
							</button>
						</div>
						<div className="form-text">
							<span
								dangerouslySetInnerHTML={{
									__html: formatMessage({
										id: "komid.smslogin.user.notYourNumber",
									}),
								}}
							/>
						</div>
					</h3>

					<form data-hj-suppress>
						<fieldset>
							<div className="digitsContainer">
								<Digit
									autoFocus={true}
									id="code-1"
									ref={register("code-1")?.ref}
									onPaste={onCodePaste}
									onKeyDown={(event) =>
										onKeyDown({ event, prevRef: null, nextRef: "code-2" })
									}
									onTextChange={(event) => {
										onTextChange(event, "code-2");
									}}
								/>
								<Digit
									id="code-2"
									ref={register("code-2")?.ref}
									onPaste={onCodePaste}
									onKeyDown={(event) =>
										onKeyDown({ event, prevRef: "code-1", nextRef: "code-3" })
									}
									onTextChange={(event) => {
										onTextChange(event, "code-3");
									}}
								/>
								<Digit
									id="code-3"
									ref={register("code-3")?.ref}
									onPaste={onCodePaste}
									onKeyDown={(event) =>
										onKeyDown({ event, prevRef: "code-2", nextRef: "code-4" })
									}
									onTextChange={(event) => {
										onTextChange(event, "code-4");
									}}
								/>
								<Digit
									id="code-4"
									ref={register("code-4")?.ref}
									onPaste={onCodePaste}
									onKeyDown={(event) =>
										onKeyDown({ event, prevRef: "code-3", nextRef: "code-5" })
									}
									onTextChange={(event) => {
										onTextChange(event, "code-5");
									}}
								/>
								<Digit
									id="code-5"
									ref={register("code-5")?.ref}
									onPaste={onCodePaste}
									onKeyDown={(event) =>
										onKeyDown({ event, prevRef: "code-4", nextRef: "code-6" })
									}
									onTextChange={(event) => {
										onTextChange(event, "code-6");
									}}
								/>
								<Digit
									id="code-6"
									ref={register("code-6")?.ref}
									onPaste={onCodePaste}
									onKeyDown={(event) =>
										onKeyDown({
											event,
											prevRef: "code-5",
											nextRef: null,
										})
									}
									onTextChange={(event) => {
										onTextChange(event, submitButtonRef);
									}}
								/>
							</div>
							<div className="form-buttons">
								<SubmitButton
									isFetching={isFetching}
									onClick={onSubmit}
									ref={submitButtonRef}
								>
									{formatMessage({
										id: "komid.smslogin.validateSmsCodeButton",
									})}
									{isFetching && <SpinningCircle className="loader" />}
								</SubmitButton>
							</div>
						</fieldset>
					</form>
					<div className="form-bottom-links">
						<p>{formatMessage({ id: "komid.login.form.forgot.password" })}</p>
						<a
							data-testid="sms.login.form.passwordless.login.link"
							href={`/password/loginlink?returnUrl=${encodeURIComponent(
								returnUrl,
							)}`}
						>
							{formatMessage({ id: "komid.login.form.forgot.password.link" })}
						</a>
					</div>
				</section>
			</div>
		</>
	);
};
